import { request } from './api';

/** Billing types (mirrors app/shared/src/billing/types.ts DTOs) and the organization-side calls. */

export type BillingAccountStatus = 'prospect' | 'trial' | 'active' | 'past_due' | 'suspended' | 'closed';
export type ServiceState = 'active' | 'grace' | 'suspended';
export type CollectionMethod = 'send_invoice' | 'charge_automatically';
export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
export type BillingInterval = 'monthly' | 'quarterly' | 'semiannual' | 'annual' | 'one_time';
export type AgreementKind = 'msa' | 'sow' | 'order_form' | 'amendment' | 'nda' | 'other';
export type AgreementStatus = 'draft' | 'sent' | 'signed' | 'active' | 'expired' | 'terminated';

export interface BillingAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface BillingAccount {
  id: string;
  status: BillingAccountStatus;
  serviceState: ServiceState;
  billingName: string | null;
  billingEmail: string | null;
  ccEmails: string[];
  address: BillingAddress;
  taxId: string | null;
  currency: string;
  paymentTermsDays: number;
  gracePeriodDays: number;
  collectionMethod: CollectionMethod;
  provider: 'stripe' | 'mock' | null;
  defaultPaymentMethodId: string | null;
  pastDueSince: string | null;
  suspendedAt: string | null;
  graceEndsOn: string | null;
}

export interface BillingStateSummary {
  configured: boolean;
  status: BillingAccountStatus | null;
  serviceState: ServiceState;
  openInvoices: number;
  amountDueCents: number;
  currency: string;
  nextDueOn: string | null;
  oldestOverdueDays: number;
  graceEndsOn: string | null;
  message: string | null;
}

export interface SubscriptionLineItem {
  id: string;
  description: string;
  kind: 'recurring' | 'one_time' | 'usage';
  quantity: number;
  unitAmountCents: number;
  usageMetric?: string;
  includedQuantity?: number;
  billed?: boolean;
}

export interface Subscription {
  id: string;
  agreementId: string | null;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'cancelled' | 'ended';
  currency: string;
  billingInterval: BillingInterval;
  lineItems: SubscriptionLineItem[];
  taxRateBps: number;
  startsOn: string;
  endsOn: string | null;
  termMonths: number | null;
  autoRenew: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextInvoiceOn: string | null;
  collectionMethod: CollectionMethod | null;
  cancelAtPeriodEnd: boolean;
  recurringAmountCents: number;
}

export interface Agreement {
  id: string;
  kind: AgreementKind;
  title: string;
  reference: string | null;
  status: AgreementStatus;
  startsOn: string | null;
  endsOn: string | null;
  termMonths: number | null;
  autoRenew: boolean;
  signedAt: string | null;
  signedByName: string | null;
  file: { id: string; name: string; contentType: string; sizeBytes: number } | null;
  notes: string | null;
}

export interface InvoiceLine {
  id: string;
  description: string;
  kind: string;
  quantity: number;
  unitAmountCents: number;
  amountCents: number;
}

export interface Payment {
  id: string;
  amountCents: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'partially_refunded';
  method: string;
  receiptNumber: string | null;
  refundedCents: number;
  reference: string | null;
  receivedAt: string;
}

export interface Invoice {
  id: string;
  number: string;
  status: InvoiceStatus;
  currency: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  amountPaidCents: number;
  amountDueCents: number;
  issuedOn: string | null;
  dueOn: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  memo: string | null;
  footer: string | null;
  collectionMethod: CollectionMethod;
  paidAt: string | null;
  voidedAt: string | null;
  daysOverdue: number | null;
  lines?: InvoiceLine[];
  payments?: Payment[];
}

export interface PaymentMethod {
  id: string;
  kind: 'card' | 'us_bank_account' | 'other';
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
}

export interface BillingNotice {
  id: string;
  invoiceId: string | null;
  kind: string;
  subject: string | null;
  bodyText: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface BillingCompany {
  name: string;
  addressLines: string[];
  email: string;
  phone: string;
  taxId: string;
  website: string;
}

export interface BillingSummaryResponse {
  configured: boolean;
  account: BillingAccount | null;
  summary?: { openInvoices: number; amountDueCents: number; overdueCents: number; oldestOverdueDueOn: string | null; oldestOverdueDays: number; nextDueOn: string | null };
  subscriptions: Subscription[];
  agreements: Agreement[];
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
  notifications: BillingNotice[];
  company?: BillingCompany;
  provider: { name: 'stripe' | 'mock'; available: boolean; portal?: boolean };
}

export interface InvoiceDetailResponse {
  invoice: Invoice;
  billedTo: { name: string; email: string | null; address: BillingAddress; taxId: string | null };
  company: BillingCompany;
}

export function fmtMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function fmtDay(date: string | null | undefined): string {
  if (!date) return '—';
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export const INTERVAL_LABEL: Record<BillingInterval, string> = { monthly: 'per month', quarterly: 'per quarter', semiannual: 'every 6 months', annual: 'per year', one_time: 'one-time' };
export const AGREEMENT_KIND_LABEL: Record<AgreementKind, string> = { msa: 'Master agreement', sow: 'Statement of work', order_form: 'Order form', amendment: 'Amendment', nda: 'NDA', other: 'Agreement' };
export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = { draft: 'Draft', open: 'Open', paid: 'Paid', void: 'Cancelled', uncollectible: 'Written off' };

export const billingApi = {
  summary: () => request<BillingSummaryResponse>('/api/v1/billing'),
  invoices: () => request<{ items: Invoice[] }>('/api/v1/billing/invoices'),
  invoice: (id: string) => request<InvoiceDetailResponse>(`/api/v1/billing/invoices/${id}`),
  pay: (id: string, saveCard: boolean) => request<{ url: string }>(`/api/v1/billing/invoices/${id}/pay`, { method: 'POST', body: { saveCard } }),
  paymentMethods: () => request<{ items: PaymentMethod[] }>('/api/v1/billing/payment-methods'),
  setupPaymentMethod: () => request<{ url: string }>('/api/v1/billing/payment-methods/setup', { method: 'POST', body: {} }),
  setDefaultPaymentMethod: (id: string) => request<{ ok: true }>(`/api/v1/billing/payment-methods/${id}/default`, { method: 'POST', body: {} }),
  removePaymentMethod: (id: string) => request<void>(`/api/v1/billing/payment-methods/${id}`, { method: 'DELETE' }),
  portal: () => request<{ url: string }>('/api/v1/billing/portal', { method: 'POST', body: {} }),
  agreementFile: (id: string) => request<{ url: string; name: string }>(`/api/v1/billing/agreements/${id}/file`),
  updateAccount: (body: { billingName?: string | null; billingEmail?: string | null; ccEmails?: string[]; address?: BillingAddress; taxId?: string | null }) => request<{ account: BillingAccount }>('/api/v1/billing/account', { method: 'PATCH', body }),
  notifications: () => request<{ items: BillingNotice[] }>('/api/v1/billing/notifications'),
  markNotificationsRead: () => request<{ updated: number }>('/api/v1/billing/notifications/read', { method: 'POST', body: {} }),
};
